import { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Loader2,
  RefreshCw,
  Sparkles,
  Film,
  Check,
  Copy,
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  AdaptedPlatformContent,
  FacebookPostSettings,
  InstagramPostSettings,
  LinkedInPostSettings,
  PlatformPostSettings,
  TwitterPostSettings,
  TwitterPollSettings,
  ThreadTweet,
  TikTokPostSettings,
  MediaAttachment,
} from '@/lib/api';
import { socialMediaApi } from '../api';
import { toast } from '@/components/ui/use-toast';
import { SegmentedControl } from './SegmentedControl';
import { TwitterUserSearch } from './TwitterUserSearch';
import { LinkedInMentionTextarea } from './LinkedInMentionTextarea';
import { PLATFORM_CHARACTER_LIMITS, ComposerPlatform } from './platform-constants';

// Platform display metadata
const PLATFORM_META: Record<ComposerPlatform, { label: string; color: string }> = {
  twitter: { label: 'X / Twitter', color: 'bg-black' },
  instagram: { label: 'Instagram', color: 'bg-gradient-to-br from-purple-500 to-pink-500' },
  linkedin: { label: 'LinkedIn', color: 'bg-blue-600' },
  facebook: { label: 'Facebook', color: 'bg-blue-500' },
  tiktok: { label: 'TikTok', color: 'bg-black' },
  youtube: { label: 'YouTube', color: 'bg-red-600' },
  telegram: { label: 'Telegram', color: 'bg-blue-400' },
};

function PlatformIcon({ platform }: { platform: ComposerPlatform }) {
  const meta = PLATFORM_META[platform] ?? { label: platform, color: 'bg-muted' };
  return (
    <div
      className={cn(
        'h-[22px] w-[22px] rounded flex items-center justify-center flex-shrink-0',
        meta.color,
      )}
      title={meta.label}
    >
      <span className="text-white text-[9px] font-bold uppercase leading-none">
        {platform === 'twitter' ? 'X' : platform.slice(0, 2).toUpperCase()}
      </span>
    </div>
  );
}

// ---- Per-platform settings components ----

const POLL_DURATION_OPTIONS: { label: string; value: number }[] = [
  { label: '5 min', value: 5 },
  { label: '30 min', value: 30 },
  { label: '1 hour', value: 60 },
  { label: '6 hours', value: 360 },
  { label: '12 hours', value: 720 },
  { label: '1 day', value: 1440 },
  { label: '2 days', value: 2880 },
  { label: '3 days', value: 4320 },
  { label: '7 days', value: 10080 },
];

function PollBuilder({
  poll,
  onChange,
  disabled,
}: {
  poll: Partial<TwitterPollSettings>;
  onChange: (p: Partial<TwitterPollSettings>) => void;
  disabled: boolean;
}) {
  const options = poll.options ?? ['', ''];
  const duration = poll.durationMinutes ?? 1440;

  const setOption = (i: number, value: string) => {
    const next = [...options];
    next[i] = value.slice(0, 25);
    onChange({ ...poll, options: next });
  };

  const addOption = () => {
    if (options.length < 4) onChange({ ...poll, options: [...options, ''] });
  };

  const removeOption = (i: number) => {
    if (options.length > 2) onChange({ ...poll, options: options.filter((_, idx) => idx !== i) });
  };

  return (
    <div className="space-y-2 pt-1">
      {options.map((opt, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <span className="text-[10px] text-muted-foreground w-3 shrink-0">{i + 1}</span>
          <input
            type="text"
            value={opt}
            onChange={(e) => setOption(i, e.target.value)}
            disabled={disabled}
            placeholder={`Option ${i + 1}`}
            maxLength={25}
            className={cn(
              'text-sm h-8',
              'w-full text-[14px] rounded-[10px] outline-none transition-all duration-150 shadow-social-card',
              'h-[40px] px-3 py-3 !font-mono',
              'text-primary',
              'bg-background  placeholder-[#171717]',
            )}
          />
          <span className="text-[10px] text-muted-foreground/50 w-6 text-right shrink-0">
            {opt.length}/25
          </span>
          {options.length > 2 && !disabled && (
            <button
              type="button"
              onClick={() => removeOption(i)}
              className="text-muted-foreground hover:text-destructive transition-colors"
            >
              <span className="text-xs leading-none">×</span>
            </button>
          )}
        </div>
      ))}
      {options.length < 4 && !disabled && (
        <button
          type="button"
          onClick={addOption}
          className="text-[10px] text-primary hover:underline"
        >
          + Add option
        </button>
      )}
      <div className="flex items-center gap-2 pt-1">
        <span className="text-xs text-muted-foreground shrink-0">Duration</span>
        <div className="relative w-full">
          <select
            value={duration}
            onChange={(e) => onChange({ ...poll, durationMinutes: Number(e.target.value) })}
            disabled={disabled}
            className={cn(
              'w-full h-[40px] px-3 pr-8 rounded-[10px] outline-none',
              'text-[12px] font-mono text-primary',
              'bg-background shadow-social-card',
              'appearance-none',
            )}
          >
            {POLL_DURATION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          {/* Custom chevron */}
          <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">▼</div>
        </div>
      </div>
    </div>
  );
}

function ThreadBuilder({
  tweets,
  onChange,
  disabled,
  mediaAttachments,
  limit,
}: {
  tweets: ThreadTweet[];
  onChange: (tweets: ThreadTweet[]) => void;
  disabled: boolean;
  mediaAttachments: MediaAttachment[];
  /** Per-tweet character limit (280 standard, 25,000 for X Premium accounts). */
  limit: number;
}) {
  const LIMIT = limit;
  const doneAttachments = mediaAttachments.filter((a) => a.uploadState === 'done');

  const setTweetText = (i: number, text: string) => {
    const next = [...tweets];
    next[i] = { ...next[i], text };
    onChange(next);
  };

  const toggleMedia = (tweetIndex: number, fileId: string) => {
    const current = tweets[tweetIndex].mediaFileIds ?? [];
    const hasIt = current.includes(fileId);
    const next = [...tweets];
    next[tweetIndex] = {
      ...next[tweetIndex],
      mediaFileIds: hasIt ? current.filter((id) => id !== fileId) : [...current, fileId],
    };
    onChange(next);
  };

  const addTweet = () => onChange([...tweets, { text: '' }]);

  const removeTweet = (i: number) => {
    if (tweets.length > 2) onChange(tweets.filter((_, idx) => idx !== i));
  };

  return (
    <div className="space-y-3">
      {tweets.map((tweet, i) => {
        const isOver = tweet.text.length > LIMIT;
        const selectedIds = tweet.mediaFileIds ?? [];
        return (
          <div key={i} className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-medium text-muted-foreground">
                {i + 1}/{tweets.length}
              </span>
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'text-[10px] font-mono',
                    isOver ? 'text-destructive' : 'text-muted-foreground/60',
                  )}
                >
                  {tweet.text.length}/{LIMIT}
                </span>
                {tweets.length > 2 && !disabled && (
                  <button
                    type="button"
                    onClick={() => removeTweet(i)}
                    className="text-muted-foreground hover:text-destructive transition-colors text-xs leading-none"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
            <Textarea
              value={tweet.text}
              onChange={(e) => setTweetText(i, e.target.value)}
              disabled={disabled}
              placeholder={i === 0 ? 'First tweet…' : `Tweet ${i + 1}…`}
              rows={5}
              className={cn(
                'text-sm resize-none min-h-[72px]',
                isOver && 'border-destructive focus-visible:ring-destructive',
                'text-sm h-8',
                'w-full text-[14px] rounded-[10px] outline-none transition-all duration-150 shadow-social-card',
                'px-3 py-3 !font-mono',
                'text-primary',
                'bg-background  placeholder-[#171717]',
              )}
            />
            {/* Per-tweet media — toggle from parent attachments */}
            {doneAttachments.length > 0 && (
              <div className="flex flex-wrap gap-1 pt-0.5">
                {doneAttachments.map((attachment) => {
                  const selected = selectedIds.includes(attachment.fileId);
                  const isImage = attachment.mimeType.startsWith('image/');
                  return (
                    <button
                      key={attachment.fileId}
                      type="button"
                      disabled={disabled}
                      onClick={() => toggleMedia(i, attachment.fileId)}
                      title={`${selected ? 'Remove' : 'Add'} ${attachment.fileName}`}
                      className={cn(
                        'relative w-9 h-9 rounded border-2 overflow-hidden transition-all flex-shrink-0',
                        selected
                          ? 'border-primary'
                          : 'border-border opacity-40 hover:opacity-70 hover:border-border/80',
                        disabled && 'pointer-events-none',
                      )}
                    >
                      {isImage ? (
                        <img
                          src={attachment.previewUrl}
                          alt={attachment.fileName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-muted">
                          <Film className="h-3.5 w-3.5 text-muted-foreground" />
                        </div>
                      )}
                      {selected && (
                        <div className="absolute inset-0 flex items-center justify-center bg-primary/25">
                          <Check className="h-3 w-3 text-primary drop-shadow-sm" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      {!disabled && (
        <button
          type="button"
          onClick={addTweet}
          className="text-[10px] text-primary hover:underline"
        >
          + Add tweet
        </button>
      )}
    </div>
  );
}

function TwitterSettingsBlock({
  settings,
  onChange,
  disabled,
  holderId,
  accountId,
  currentText,
  onEditedTextChange,
  onEnhanceThread,
  isEnhancingThread,
  mediaAttachments,
  limit,
}: {
  settings: Partial<TwitterPostSettings>;
  onChange: (s: Partial<TwitterPostSettings>) => void;
  disabled: boolean;
  /** Vault Holder ID — required by the Twitter user-search API. */
  holderId: string | null;
  accountId?: string;
  /** Current display text — used to seed the thread's first tweet when switching to thread format */
  currentText?: string;
  /** Syncs thread tweet[0] back to the parent's editedText state */
  onEditedTextChange?: (text: string) => void;
  /** Triggers AI enhancement of the full thread */
  onEnhanceThread?: () => void;
  isEnhancingThread?: boolean;
  /** Available media attachments for per-tweet media selection */
  mediaAttachments: MediaAttachment[];
  /** Per-tweet character limit forwarded to the thread builder. */
  limit: number;
}) {
  // tagUser is stored as space-separated "@username" strings, e.g. "@alice @bob"
  const selectedUsernames = (settings.tagUser ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .map((u) => u.replace(/^@/, ''));

  const handleUsernamesChange = (usernames: string[]) => {
    const added = usernames.filter((u) => !selectedUsernames.includes(u));
    const removed = selectedUsernames.filter((u) => !usernames.includes(u));

    onChange({
      ...settings,
      tagUser: usernames.length > 0 ? usernames.map((u) => `@${u}`).join(' ') : undefined,
    });

    // Keep @mentions in sync with the tweet text so the tag is visible in the post.
    // Twitter mentions must appear in the tweet body to notify the tagged user.
    if (onEditedTextChange && currentText !== undefined) {
      let text = currentText;

      for (const username of added) {
        const mention = `@${username}`;
        if (!text.includes(mention)) {
          text = text ? `${text} ${mention}` : mention;
        }
      }

      for (const username of removed) {
        // Remove the mention along with any surrounding whitespace
        text = text.replace(new RegExp(`\\s*@${username}\\b`, 'g'), '').trim();
      }

      if (text !== currentText) {
        onEditedTextChange(text);
      }
    }
  };

  const handleFormatChange = (v: string) => {
    const format = v as TwitterPostSettings['format'];
    onChange({
      ...settings,
      format,
      // When switching to thread, seed the thread with the current text
      threadTweets: format === 'thread' ? [{ text: currentText ?? '' }, { text: '' }] : undefined,
      poll:
        format === 'poll'
          ? (settings.poll ?? { options: ['', ''], durationMinutes: 1440 })
          : undefined,
      replyToUrl: format === 'reply' ? settings.replyToUrl : undefined,
    });
  };

  const handleThreadChange = (tweets: ThreadTweet[]) => {
    onChange({ ...settings, threadTweets: tweets });
    // Keep tweet[0] in sync with the parent's editedText
    onEditedTextChange?.(tweets[0]?.text ?? '');
  };

  const isThread = settings.format === 'thread';

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Format</span>
        <SegmentedControl
          options={[
            { value: 'tweet', label: 'Tweet' },
            { value: 'thread', label: 'Thread' },
            { value: 'poll', label: 'Poll' },
            { value: 'reply', label: 'Reply' },
          ]}
          value={settings.format ?? 'tweet'}
          onChange={handleFormatChange}
          disabled={disabled}
          size="xs"
        />
      </div>

      {/* Thread builder — only when format is thread */}
      {isThread && (
        <>
          <ThreadBuilder
            tweets={settings.threadTweets ?? [{ text: currentText ?? '' }, { text: '' }]}
            onChange={handleThreadChange}
            disabled={disabled}
            mediaAttachments={mediaAttachments}
            limit={limit}
          />
          {onEnhanceThread && !disabled && (
            <button
              type="button"
              onClick={onEnhanceThread}
              disabled={isEnhancingThread}
              className="flex items-center gap-1.5 text-xs text-primary hover:underline disabled:opacity-50 disabled:no-underline"
            >
              <Sparkles className="h-3 w-3" />
              {isEnhancingThread ? 'Enhancing…' : 'Enhance thread with AI'}
            </button>
          )}
        </>
      )}

      {/* Poll builder — only when format is poll */}
      {settings.format === 'poll' && (
        <div className="space-y-1.5">
          <span className="text-xs text-muted-foreground">Poll options</span>
          <PollBuilder
            poll={settings.poll ?? { options: ['', ''], durationMinutes: 1440 }}
            onChange={(p) => onChange({ ...settings, poll: p as TwitterPollSettings })}
            disabled={disabled}
          />
        </div>
      )}

      {/* Reply to — only when format is reply */}
      {settings.format === 'reply' && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground shrink-0">Reply to</span>
          <input
            type="text"
            value={settings.replyToUrl ?? ''}
            onChange={(e) => onChange({ ...settings, replyToUrl: e.target.value })}
            disabled={disabled}
            placeholder="Tweet URL or ID"
            className={cn(
              'text-xs h-8',
              'w-full rounded-[10px] outline-none transition-all duration-150 shadow-social-card',
              'h-[40px] px-3 py-3 !font-mono',
              'text-primary',
              'bg-background  placeholder-[#171717]',
            )}
          />
        </div>
      )}

      {/* Include link — not applicable for poll or thread */}
      {settings.format !== 'poll' && !isThread && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Include link card</span>
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange({ ...settings, includeLink: !settings.includeLink })}
            className={cn(
              'px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors',
              settings.includeLink
                ? 'bg-primary text-primary-foreground border-primary'
                : 'border-border text-muted-foreground hover:border-foreground/40',
            )}
          >
            {settings.includeLink ? 'On' : 'Off'}
          </button>
        </div>
      )}

      {/* Tag users — not applicable for poll or thread */}
      {settings.format !== 'poll' && !isThread && (
        <div className="space-y-1.5">
          <span className="text-xs text-muted-foreground">Tag users</span>
          <TwitterUserSearch
            holderId={holderId}
            accountId={accountId}
            selectedUsernames={selectedUsernames}
            onChange={handleUsernamesChange}
            disabled={disabled}
          />
          {selectedUsernames.length > 0 && (
            <p className="text-[10px] text-muted-foreground">
              Mentions added to your tweet text automatically.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function InstagramSettingsBlock({
  settings,
  onChange,
  disabled,
  mediaAttachments,
}: {
  settings: Partial<InstagramPostSettings>;
  onChange: (s: Partial<InstagramPostSettings>) => void;
  disabled: boolean;
  mediaAttachments: MediaAttachment[];
}) {
  const hasVideo = mediaAttachments.some((a) => a.mimeType.startsWith('video/'));
  const imageAttachments = mediaAttachments.filter((a) => a.mimeType.startsWith('image/'));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Format</span>
        <SegmentedControl
          options={[
            { value: 'post', label: 'Post' },
            { value: 'reel', label: 'Reel' },
            { value: 'carousel', label: 'Carousel' },
            { value: 'story', label: 'Story' },
          ]}
          value={settings.format ?? 'post'}
          onChange={(v) => onChange({ ...settings, format: v })}
          disabled={disabled}
          size="xs"
        />
      </div>
      {settings.format === 'reel' && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Cover image</span>
          <div className="flex items-center gap-2">
            {settings.coverImageFileId && imageAttachments.length > 0 && (
              <select
                value={settings.coverImageFileId}
                onChange={(e) => onChange({ ...settings, coverImageFileId: e.target.value })}
                disabled={disabled}
                className="h-7 rounded-md border border-input bg-background px-2 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
              >
                {imageAttachments.map((a) => (
                  <option key={a.fileId} value={a.fileId}>
                    {a.fileName}
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              disabled={disabled || imageAttachments.length === 0}
              onClick={() =>
                onChange({
                  ...settings,
                  coverImageFileId: settings.coverImageFileId
                    ? undefined
                    : imageAttachments[0]?.fileId,
                })
              }
              className={cn(
                'px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors',
                settings.coverImageFileId
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:border-foreground/40',
                (disabled || imageAttachments.length === 0) && 'opacity-50 cursor-not-allowed',
              )}
            >
              {settings.coverImageFileId ? 'On' : 'Off'}
            </button>
          </div>
        </div>
      )}
      {settings.format === 'reel' && !hasVideo && (
        <p className="text-xs text-amber-600">Reel format works best with a video file.</p>
      )}
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Hashtags in first comment</span>
        <button
          type="button"
          disabled={disabled}
          onClick={() =>
            onChange({ ...settings, hashtagsInFirstComment: !settings.hashtagsInFirstComment })
          }
          className={cn(
            'px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors',
            settings.hashtagsInFirstComment
              ? 'bg-primary text-primary-foreground border-primary'
              : 'border-border text-muted-foreground hover:border-foreground/40',
          )}
        >
          {settings.hashtagsInFirstComment ? 'On' : 'Off'}
        </button>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground shrink-0">Collaborator</span>
        <input
          type="text"
          value={settings.collaboratorTag ?? ''}
          onChange={(e) => onChange({ ...settings, collaboratorTag: e.target.value })}
          disabled={disabled}
          placeholder="@username"
          className="flex-1 h-7 rounded-md border border-input bg-background px-2 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
        />
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground shrink-0">Location</span>
        <input
          type="text"
          value={settings.locationName ?? ''}
          onChange={(e) => onChange({ ...settings, locationName: e.target.value })}
          disabled={disabled}
          placeholder="Add location"
          className="flex-1 h-7 rounded-md border border-input bg-background px-2 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
        />
      </div>
      {mediaAttachments.length > 1 && (
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">Media order (drag or select primary)</p>
          <div className="flex flex-wrap gap-1">
            {mediaAttachments.map((a, i) => (
              <span
                key={a.fileId}
                className="text-xs border border-border rounded px-1.5 py-0.5 text-muted-foreground"
              >
                {i + 1}. {a.fileName.slice(0, 16)}
                {a.fileName.length > 16 ? '…' : ''}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const LINKEDIN_MAX_IMAGES = 9;

function LinkedInSettingsBlock({
  settings,
  onChange,
  disabled,
  mediaAttachments,
}: {
  settings: Partial<LinkedInPostSettings>;
  onChange: (s: Partial<LinkedInPostSettings>) => void;
  disabled: boolean;
  mediaAttachments: MediaAttachment[];
}) {
  // Only show fully-uploaded files (same pattern as ThreadEditorBlock)
  const doneAttachments = mediaAttachments.filter((a) => a.uploadState === 'done');

  const selectedIds = settings.selectedMediaFileIds ?? [];

  // Derive what category the current selection resolves to
  const selectedAttachments = doneAttachments.filter((a) => selectedIds.includes(a.fileId));
  const hasSelectedVideo = selectedAttachments.some((a) => a.mimeType.startsWith('video/'));
  const selectedImageCount = selectedAttachments.filter((a) =>
    a.mimeType.startsWith('image/'),
  ).length;

  const handleToggle = (attachment: MediaAttachment) => {
    const isVideo = attachment.mimeType.startsWith('video/');
    const isSelected = selectedIds.includes(attachment.fileId);

    if (isSelected) {
      // Deselect
      const next = selectedIds.filter((id) => id !== attachment.fileId);
      onChange({ ...settings, selectedMediaFileIds: next.length > 0 ? next : undefined });
      return;
    }

    if (isVideo) {
      // Selecting a video: enforce 1-video-only — replace entire selection with this video
      onChange({ ...settings, selectedMediaFileIds: [attachment.fileId] });
      return;
    }

    // Selecting an image
    if (hasSelectedVideo) {
      // Switch from video mode to image mode — deselect video, select this image
      onChange({ ...settings, selectedMediaFileIds: [attachment.fileId] });
      return;
    }

    if (selectedImageCount >= LINKEDIN_MAX_IMAGES) {
      // Already at 9-image cap — do nothing (button is visually blocked below)
      return;
    }

    onChange({ ...settings, selectedMediaFileIds: [...selectedIds, attachment.fileId] });
  };

  // Determine which files are blocked (cannot be added to the current selection)
  const isBlocked = (attachment: MediaAttachment): boolean => {
    if (selectedIds.includes(attachment.fileId)) return false; // already selected — can deselect
    const isVideo = attachment.mimeType.startsWith('video/');
    if (!isVideo && selectedImageCount >= LINKEDIN_MAX_IMAGES) return true; // image cap reached
    return false;
  };

  // Badge label for the resulting LinkedIn media category
  const categoryLabel =
    selectedAttachments.length === 0
      ? null
      : hasSelectedVideo
        ? 'VIDEO'
        : selectedImageCount > 1
          ? `IMAGE (${selectedImageCount})`
          : 'IMAGE';

  return (
    <div className="space-y-3">
      {/* <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Post type</span>
        <SegmentedControl
          options={[
            { value: 'post', label: 'Post' },
            { value: 'article', label: 'Article' },
            { value: 'event', label: 'Event' },
          ]}
          value={settings.postType ?? 'post'}
          onChange={(v) => onChange({ ...settings, postType: v })}
          disabled={disabled}
          size="xs"
        />
      </div> */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Include link preview</span>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange({ ...settings, includeLink: !settings.includeLink })}
          className={cn(
            'px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors',
            settings.includeLink
              ? 'bg-primary text-primary-foreground border-primary'
              : 'border-border text-muted-foreground hover:border-foreground/40',
          )}
        >
          {settings.includeLink ? 'On' : 'Off'}
        </button>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Visibility</span>
        <SegmentedControl
          options={[
            { value: 'PUBLIC', label: 'Public' },
            { value: 'CONNECTIONS', label: 'Connections' },
            { value: 'LOGGED_IN', label: 'Logged in' },
          ]}
          value={settings.visibility ?? 'PUBLIC'}
          onChange={(v) =>
            onChange({ ...settings, visibility: v as 'PUBLIC' | 'CONNECTIONS' | 'LOGGED_IN' })
          }
          disabled={disabled}
          size="xs"
        />
      </div>

      {/* Media file selector — only shown when there are fully-uploaded files to choose from */}
      {doneAttachments.length > 0 && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Media</span>
            {categoryLabel && (
              <span className="text-[10px] font-medium text-blue-600 dark:text-blue-400">
                {categoryLabel}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {doneAttachments.map((attachment) => {
              const isSelected = selectedIds.includes(attachment.fileId);
              const isImage = attachment.mimeType.startsWith('image/');
              const blocked = !isSelected && isBlocked(attachment);
              return (
                <button
                  key={attachment.fileId}
                  type="button"
                  disabled={disabled || blocked}
                  onClick={() => handleToggle(attachment)}
                  title={
                    blocked
                      ? `Cannot add — ${hasSelectedVideo ? 'deselect the video first' : 'image limit (9) reached'}`
                      : `${isSelected ? 'Remove' : 'Add'} ${attachment.fileName}`
                  }
                  className={cn(
                    'relative w-10 h-10 rounded border-2 overflow-hidden transition-all flex-shrink-0',
                    isSelected
                      ? 'border-primary'
                      : blocked
                        ? 'border-border opacity-20 cursor-not-allowed'
                        : 'border-border opacity-40 hover:opacity-70 hover:border-border/80',
                    disabled && 'pointer-events-none',
                  )}
                >
                  {isImage && attachment.previewUrl ? (
                    <img
                      src={attachment.previewUrl}
                      alt={attachment.fileName}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-muted">
                      <Film className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                  )}
                  {isSelected && (
                    <div className="absolute inset-0 flex items-center justify-center bg-primary/25">
                      <Check className="h-3 w-3 text-primary drop-shadow-sm" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          <p className="text-[10px] text-muted-foreground">
            {hasSelectedVideo
              ? 'Video selected — deselect to switch to images.'
              : selectedImageCount >= LINKEDIN_MAX_IMAGES
                ? `${LINKEDIN_MAX_IMAGES} image limit reached.`
                : 'Select images (up to 9) or 1 video. Selecting a video clears images and vice versa.'}
          </p>
        </div>
      )}
    </div>
  );
}

function FacebookSettingsBlock({
  settings,
  onChange,
  disabled,
}: {
  settings: Partial<FacebookPostSettings>;
  onChange: (s: Partial<FacebookPostSettings>) => void;
  disabled: boolean;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Format</span>
        <SegmentedControl
          options={[
            { value: 'post', label: 'Post' },
            { value: 'reel', label: 'Reel' },
            { value: 'story', label: 'Story' },
            { value: 'event', label: 'Event' },
          ]}
          value={settings.format ?? 'post'}
          onChange={(v) => onChange({ ...settings, format: v })}
          disabled={disabled}
          size="xs"
        />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Include link preview</span>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange({ ...settings, includeLink: !settings.includeLink })}
          className={cn(
            'px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors',
            settings.includeLink
              ? 'bg-primary text-primary-foreground border-primary'
              : 'border-border text-muted-foreground hover:border-foreground/40',
          )}
        >
          {settings.includeLink ? 'On' : 'Off'}
        </button>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Audience</span>
        <SegmentedControl
          options={[
            { value: 'public', label: 'Public' },
            { value: 'friends', label: 'Friends' },
            { value: 'custom', label: 'Custom' },
          ]}
          value={settings.audience ?? 'public'}
          onChange={(v) => onChange({ ...settings, audience: v })}
          disabled={disabled}
          size="xs"
        />
      </div>
    </div>
  );
}

function TikTokSettingsBlock({
  settings,
  onChange,
  disabled,
  hasVideo,
}: {
  settings: Partial<TikTokPostSettings>;
  onChange: (s: Partial<TikTokPostSettings>) => void;
  disabled: boolean;
  hasVideo: boolean;
}) {
  return (
    <div className="space-y-3">
      {!hasVideo && <p className="text-xs text-destructive">TikTok requires a video file</p>}
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Post type</span>
        <SegmentedControl
          options={[
            { value: 'video', label: 'Video' },
            { value: 'photo_mode', label: 'Photo mode' },
            { value: 'live', label: 'Live' },
          ]}
          value={settings.postType ?? 'video'}
          onChange={(v) => onChange({ ...settings, postType: v })}
          disabled={disabled}
          size="xs"
        />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Who can view</span>
        <SegmentedControl
          options={[
            { value: 'everyone', label: 'Everyone' },
            { value: 'friends', label: 'Friends' },
            { value: 'private', label: 'Private' },
          ]}
          value={settings.whoCanView ?? 'everyone'}
          onChange={(v) => onChange({ ...settings, whoCanView: v })}
          disabled={disabled}
          size="xs"
        />
      </div>
      {(['allowDuets', 'allowStitch', 'disclosePaidPartnership'] as const).map((field) => {
        const labels: Record<string, string> = {
          allowDuets: 'Allow duets',
          allowStitch: 'Allow stitch',
          disclosePaidPartnership: 'Disclose paid partnership',
        };
        return (
          <div key={field} className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{labels[field]}</span>
            <button
              type="button"
              disabled={disabled}
              onClick={() => onChange({ ...settings, [field]: !settings[field] })}
              className={cn(
                'px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors',
                settings[field]
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:border-foreground/40',
              )}
            >
              {settings[field] ? 'On' : 'Off'}
            </button>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Shown under the Twitter editor when a single tweet runs over the account's
 * effective character limit. Offers two AI affordances the user asked for:
 * condense the post to fit one tweet, or split it into a thread.
 */
function TwitterFitHelper({
  charCount,
  limit,
  onCondense,
  onSplit,
  isCondensing,
  isSplitting,
  disabled,
}: {
  charCount: number;
  limit: number;
  onCondense: () => void;
  onSplit: () => void;
  isCondensing: boolean;
  isSplitting: boolean;
  disabled: boolean;
}) {
  const over = charCount - limit;
  const busy = isCondensing || isSplitting;
  const buttonClass = cn(
    'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
    'border-border bg-background text-foreground hover:border-primary/50 hover:text-primary',
    'disabled:opacity-50 disabled:cursor-not-allowed',
  );
  return (
    <div className="rounded-md border border-destructive/30 bg-destructive/5 p-2.5 space-y-2">
      <p className="text-xs text-destructive">
        {over.toLocaleString()} character{over === 1 ? '' : 's'} over X&apos;s{' '}
        {limit.toLocaleString()} limit. Let AI fix it:
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onCondense}
          disabled={disabled || busy}
          className={buttonClass}
        >
          {isCondensing ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Sparkles className="h-3 w-3" />
          )}
          {isCondensing ? 'Shortening…' : 'Shorten to fit'}
        </button>
        <button type="button" onClick={onSplit} disabled={disabled || busy} className={buttonClass}>
          {isSplitting ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Sparkles className="h-3 w-3" />
          )}
          {isSplitting ? 'Splitting…' : 'Split into thread'}
        </button>
      </div>
    </div>
  );
}

// ---- Main PlatformCard ----

export interface PlatformCardProps {
  platform: ComposerPlatform;
  handle?: string;
  /** Vault Holder ID — required for thread-enhance + per-platform search APIs. */
  holderId: string | null;
  /** Post ID — used for thread enhancement API calls */
  postId?: string;
  /** accountId of the connected account for this platform (used for Twitter user search) */
  platformAccountId?: string;
  /** Link URL from the composer — appended to tweet text when includeLink is toggled on */
  link?: string;
  /** Original post content — shown as editable text until AI adaptation is generated */
  originalContent?: string;
  aiContent?: AdaptedPlatformContent;
  editedText: string;
  onEditedTextChange: (text: string) => void;
  isApproved: boolean;
  onApprove: (approved: boolean) => void;
  settings: Partial<PlatformPostSettings>;
  onSettingsChange: (settings: Partial<PlatformPostSettings>) => void;
  onRegenerate?: () => void;
  isAdapting?: boolean;
  disabled?: boolean;
  mediaAttachments: MediaAttachment[];
  /** AI context (tone, goal, audience) forwarded to thread enhancement */
  aiContext?: { tone?: string[]; goal?: string[]; audience?: string };
  /**
   * Effective character limit for this platform's connected account. Overrides
   * the per-platform default — used to grant X Premium accounts the 25,000-char
   * long-form limit instead of the standard 280.
   */
  characterLimit?: number;
}

export function PlatformCard({
  platform,
  handle,
  holderId,
  postId,
  platformAccountId,
  link,
  originalContent,
  aiContent,
  editedText,
  onEditedTextChange,
  isApproved,
  onApprove,
  settings,
  onSettingsChange,
  onRegenerate,
  isAdapting = false,
  disabled = false,
  mediaAttachments,
  aiContext,
  characterLimit,
}: PlatformCardProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [isEnhancingThread, setIsEnhancingThread] = useState(false);
  const [isCondensing, setIsCondensing] = useState(false);
  const [isSplitting, setIsSplitting] = useState(false);
  const [copiedHashtags, setCopiedHashtags] = useState(false);
  const meta = PLATFORM_META[platform] ?? { label: platform, color: 'bg-muted' };
  const limit = characterLimit ?? PLATFORM_CHARACTER_LIMITS[platform] ?? 280;
  const hasAiContent = !!aiContent;
  // Before AI generation use the original post content; after, use the AI-edited text
  const displayText = editedText || originalContent || '';
  const charCount = displayText.length;
  const isOverLimit = charCount > limit;
  const hasVideo = mediaAttachments.some((a) => a.mimeType.startsWith('video/'));
  // Thread format renders its own text areas inside TwitterSettingsBlock — hide the main one
  const isTwitterThread =
    platform === 'twitter' && (settings as Partial<TwitterPostSettings>).format === 'thread';
  const threadTweets: ThreadTweet[] = (settings as Partial<TwitterPostSettings>).threadTweets ?? [];

  const isLinkedIn = platform === 'linkedin';
  const linkedInSettings = isLinkedIn ? (settings as Partial<LinkedInPostSettings>) : null;
  const linkedInMentions = linkedInSettings?.mentions ?? [];
  const handleLinkedInMentionsChange = (
    updated: Array<{ name: string; urn: string; type: 'organization' }>,
  ) => {
    onSettingsChange({ ...settings, mentions: updated } as Partial<PlatformPostSettings>);
  };

  const showCharCount = !isTwitterThread && (platform === 'facebook' ? charCount > 1000 : true);

  const handleEnhanceThread = async () => {
    if (!postId || !holderId || threadTweets.length === 0) return;
    setIsEnhancingThread(true);
    try {
      const result = await socialMediaApi.enhanceTwitterThread(
        holderId,
        postId,
        threadTweets.map((t) => t.text),
        {
          tone: aiContext?.tone,
          goal: aiContext?.goal,
          audience: aiContext?.audience,
        },
      );
      // Re-map enhanced text back onto ThreadTweet objects, preserving per-tweet mediaFileIds
      const enhanced: ThreadTweet[] = result.tweets.map((text, i) => ({
        text,
        mediaFileIds: threadTweets[i]?.mediaFileIds,
      }));
      onSettingsChange({
        ...settings,
        threadTweets: enhanced,
      } as Partial<PlatformPostSettings>);
      onEditedTextChange(enhanced[0]?.text ?? '');
    } catch {
      // Enhancement failed silently — tweets remain unchanged
    } finally {
      setIsEnhancingThread(false);
    }
  };

  // AI context forwarded to the condense / split helpers so the rewrite matches
  // the tone, goal, and audience the user already dialled in.
  const fitContext = {
    tone: aiContext?.tone,
    goal: aiContext?.goal,
    audience: aiContext?.audience,
  };

  // "Shorten to fit" — AI rewrites the current text to land under the limit.
  const handleCondense = async () => {
    if (!holderId || !displayText.trim() || isCondensing || isSplitting) return;
    setIsCondensing(true);
    try {
      const result = await socialMediaApi.condenseTwitterPost(
        holderId,
        displayText,
        limit,
        fitContext,
      );
      const shortened = result.text?.trim();
      if (!shortened) {
        toast({
          variant: 'destructive',
          description: "Couldn't shorten the post. Please try again.",
        });
        return;
      }
      onEditedTextChange(shortened);
    } catch {
      toast({
        variant: 'destructive',
        description: "Couldn't shorten the post. Please try again.",
      });
    } finally {
      setIsCondensing(false);
    }
  };

  // "Split into thread" — AI breaks the post into tweets and switches the card
  // to thread format, populating the thread builder with the result.
  const handleSplitThread = async () => {
    if (!holderId || !displayText.trim() || isCondensing || isSplitting) return;
    setIsSplitting(true);
    try {
      const result = await socialMediaApi.splitTwitterThread(
        holderId,
        displayText,
        limit,
        fitContext,
      );
      const tweets: ThreadTweet[] = (result.tweets ?? [])
        .map((t) => t.trim())
        .filter((t) => t.length > 0)
        .map((text) => ({ text }));
      if (tweets.length === 0) {
        toast({
          variant: 'destructive',
          description: "Couldn't split into a thread. Please try again.",
        });
        return;
      }
      // Thread format needs at least two slots — pad with an empty tweet if the
      // model returned a single one.
      const threadTweets = tweets.length >= 2 ? tweets : [...tweets, { text: '' }];
      onSettingsChange({
        ...(settings as Partial<TwitterPostSettings>),
        format: 'thread',
        threadTweets,
      } as Partial<PlatformPostSettings>);
      onEditedTextChange(threadTweets[0]?.text ?? '');
    } catch {
      toast({
        variant: 'destructive',
        description: "Couldn't split into a thread. Please try again.",
      });
    } finally {
      setIsSplitting(false);
    }
  };

  // Over-limit notice. For X we surface the AI shorten / split affordances; other
  // platforms keep the plain warning text.
  const overLimitNotice = !isOverLimit ? null : platform === 'twitter' ? (
    <TwitterFitHelper
      charCount={charCount}
      limit={limit}
      onCondense={handleCondense}
      onSplit={handleSplitThread}
      isCondensing={isCondensing}
      isSplitting={isSplitting}
      disabled={disabled}
    />
  ) : (
    <p className="text-xs text-destructive">
      Exceeds {limit}-character limit for {meta.label}.
    </p>
  );

  return (
    <div
      className={cn(
        'rounded-lg bg-background transition-colors shadow-social-card',
        isApproved && 'border-primary/40',
      )}
    >
      {/* Card header */}
      <div
        className="flex items-center justify-between px-3 py-2.5 cursor-pointer select-none"
        onClick={() => setCollapsed((c) => !c)}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <PlatformIcon platform={platform} />
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground leading-tight">{meta.label}</p>
            {handle && (
              <p className="text-xs text-muted-foreground leading-tight truncate">@{handle}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Thread: show tweet count badge */}
          {isTwitterThread && threadTweets.length > 0 && (
            <span className="text-xs font-mono px-1.5 py-0.5 rounded border text-muted-foreground border-border">
              {threadTweets.length} tweets
            </span>
          )}
          {/* Single post: show char count */}
          {showCharCount && (
            <span
              className={cn(
                'text-xs font-mono px-1.5 py-0.5 rounded border',
                isOverLimit
                  ? 'text-destructive border-destructive/30 bg-destructive/5'
                  : 'text-muted-foreground border-border',
              )}
            >
              {charCount}/{limit}
            </span>
          )}

          {/* Approve toggle — always shown so settings are committed before publish */}
          {!disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onApprove(!isApproved);
              }}
              className={cn(
                'px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors',
                isApproved
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:border-foreground/40',
              )}
            >
              {isApproved ? 'Approved' : 'Approve'}
            </button>
          )}

          {collapsed ? (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </div>

      {/* Card body */}
      {!collapsed && (
        <div className="border-t border-border border-dashed px-3 pb-3 pt-3 space-y-4">
          {/* Settings section */}
          <div>
            {platform === 'twitter' && (
              <TwitterSettingsBlock
                settings={settings as Partial<TwitterPostSettings>}
                onChange={(s) => {
                  onSettingsChange(s as Partial<PlatformPostSettings>);
                  // Append/remove link in tweet text when includeLink is toggled
                  if (link) {
                    const prevIncluded = !!(settings as Partial<TwitterPostSettings>).includeLink;
                    const nextIncluded = !!s.includeLink;
                    if (!prevIncluded && nextIncluded && !displayText.includes(link)) {
                      onEditedTextChange(displayText ? `${displayText}\n\n${link}` : link);
                    } else if (prevIncluded && !nextIncluded && displayText.includes(link)) {
                      onEditedTextChange(
                        displayText.replace(`\n\n${link}`, '').replace(link, '').trimEnd(),
                      );
                    }
                  }
                }}
                disabled={disabled}
                holderId={holderId}
                accountId={platformAccountId}
                currentText={displayText}
                onEditedTextChange={onEditedTextChange}
                onEnhanceThread={postId ? handleEnhanceThread : undefined}
                isEnhancingThread={isEnhancingThread}
                mediaAttachments={mediaAttachments}
                limit={limit}
              />
            )}
            {platform === 'instagram' && (
              <InstagramSettingsBlock
                settings={settings as Partial<InstagramPostSettings>}
                onChange={(s) => onSettingsChange(s as Partial<PlatformPostSettings>)}
                disabled={disabled}
                mediaAttachments={mediaAttachments}
              />
            )}
            {platform === 'linkedin' && (
              <LinkedInSettingsBlock
                settings={settings as Partial<LinkedInPostSettings>}
                onChange={(s) => {
                  onSettingsChange(s as Partial<PlatformPostSettings>);
                  // Append/remove link in post text when includeLink is toggled
                  if (link) {
                    const prevIncluded = !!(settings as Partial<LinkedInPostSettings>).includeLink;
                    const nextIncluded = !!(s as Partial<LinkedInPostSettings>).includeLink;
                    if (!prevIncluded && nextIncluded && !displayText.includes(link)) {
                      onEditedTextChange(displayText ? `${displayText}\n\n${link}` : link);
                    } else if (prevIncluded && !nextIncluded && displayText.includes(link)) {
                      onEditedTextChange(
                        displayText.replace(`\n\n${link}`, '').replace(link, '').trimEnd(),
                      );
                    }
                  }
                }}
                disabled={disabled}
                mediaAttachments={mediaAttachments}
              />
            )}
            {platform === 'facebook' && (
              <FacebookSettingsBlock
                settings={settings as Partial<FacebookPostSettings>}
                onChange={(s) => onSettingsChange(s as Partial<PlatformPostSettings>)}
                disabled={disabled}
              />
            )}
            {platform === 'tiktok' && (
              <TikTokSettingsBlock
                settings={settings as Partial<TikTokPostSettings>}
                onChange={(s) => onSettingsChange(s as Partial<PlatformPostSettings>)}
                disabled={disabled}
                hasVideo={hasVideo}
              />
            )}
          </div>

          {/* Editable text — hidden when Twitter thread format (thread builder is inside settings) */}
          {!isTwitterThread && !hasAiContent && (
            <div className="space-y-1.5">
              {isLinkedIn ? (
                <LinkedInMentionTextarea
                  value={displayText}
                  onChange={onEditedTextChange}
                  mentions={linkedInMentions}
                  onMentionsChange={handleLinkedInMentionsChange}
                  holderId={holderId}
                  accountId={platformAccountId}
                  disabled={disabled}
                  rows={4}
                />
              ) : (
                <Textarea
                  value={displayText}
                  onChange={(e) => onEditedTextChange(e.target.value)}
                  rows={4}
                  className={cn(
                    'text-sm resize-none text-sm bg-background',
                    'w-full text-[14px] rounded-[10px] outline-none transition-all duration-150 shadow-social-card',
                    'px-3 py-3 !font-mono',
                    'text-primary',
                    'bg-background  placeholder-[#171717]',
                  )}
                  disabled={disabled}
                  readOnly={disabled}
                  placeholder="Post content will appear here…"
                />
              )}
              {overLimitNotice}
            </div>
          )}
          {!isTwitterThread && hasAiContent && (
            <div className="rounded-md bg-muted/50 border border-border p-3 space-y-2.5">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-muted-foreground">
                  AI adapted · {meta.label}
                </p>
                {onRegenerate && !disabled && (
                  <button
                    type="button"
                    onClick={onRegenerate}
                    disabled={isAdapting}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isAdapting ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3 w-3" />
                    )}
                    {isAdapting ? 'Regenerating…' : 'Regenerate'}
                  </button>
                )}
              </div>

              {isLinkedIn ? (
                <LinkedInMentionTextarea
                  value={editedText}
                  onChange={onEditedTextChange}
                  mentions={linkedInMentions}
                  onMentionsChange={handleLinkedInMentionsChange}
                  holderId={holderId}
                  accountId={platformAccountId}
                  disabled={disabled}
                  rows={4}
                />
              ) : (
                <Textarea
                  value={editedText}
                  onChange={(e) => onEditedTextChange(e.target.value)}
                  rows={4}
                  className={cn(
                    'text-sm resize-none text-sm bg-background',
                    'w-full text-[14px] rounded-[10px] outline-none transition-all duration-150 shadow-social-card',
                    'px-3 py-3 !font-mono',
                    'text-primary',
                    'bg-background  placeholder-[#171717]',
                  )}
                  disabled={disabled}
                  readOnly={disabled}
                />
              )}

              {overLimitNotice}

              {aiContent.suggestions.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">AI Tips</p>
                  <ul className="space-y-1">
                    {aiContent.suggestions.map((tip, i) => (
                      <li key={i} className="text-xs text-muted-foreground flex gap-1">
                        <span className="text-primary shrink-0">•</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {aiContent.hashtags.length > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-muted-foreground">Suggested Hashtags</p>
                    <button
                      type="button"
                      onClick={() => {
                        const hashtagString = aiContent.hashtags
                          .map((h) => (h.startsWith('#') ? h : `#${h}`))
                          .join(' ');
                        void navigator.clipboard.writeText(hashtagString).then(() => {
                          setCopiedHashtags(true);
                          setTimeout(() => setCopiedHashtags(false), 2000);
                        });
                      }}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {copiedHashtags ? (
                        <Check className="h-3 w-3 text-primary" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                      {copiedHashtags ? 'Copied!' : 'Copy all'}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {aiContent.hashtags.map((tag, i) => {
                      const display = tag.startsWith('#') ? tag : `#${tag}`;
                      return (
                        <span
                          key={i}
                          className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-mono text-primary"
                        >
                          {display}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
